// src/components/WellInfoPanel.jsx

import React, { useState } from 'react';
import { usePanelEnrichment } from '../hooks/usePanelEnrichment';
import WellDirectionalSection from './WellDirectionalSection';

function fmt(n, decimals = 0) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: decimals });
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function extractFormation(fieldName) {
  if (!fieldName) return null;
  const match = fieldName.match(/\(([^)]+)\)/);
  return match ? match[1] : null;
}

function getFormationSource(data) {
  const raw = data?.completion?.primary_formation
    ?? data?.completion?.formations?.find(f => f.productive)?.formation_name
    ?? data?.completion?.formations?.[0]?.formation_name
    ?? extractFormation(data?.field_name)
    ?? null;
  if (!raw) return null;
  return raw.length > 22 ? raw.slice(0, 21) + '…' : raw;
}

const STATUS_COLORS = {
  green: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50',
  amber: 'bg-amber-900/60  text-amber-300  border-amber-700/50',
  red: 'bg-red-900/60    text-red-300    border-red-700/50',
  blue: 'bg-blue-900/60   text-blue-300   border-blue-700/50',
  gray: 'bg-slate-800     text-slate-400  border-slate-700',
};

// RRC prorates gas per well and oil per lease. Measured across the well
// base: gas averages 1.00 wells per lease, oil averages 8.66. So for a gas
// well "lease production" IS this well's production, and only oil needs the
// aggregation caveat. 80% of oil leases hold 10 wells or fewer, but 387 hold
// 100+ (the largest, SACROC, has 2,553) — hence the actual count is shown
// rather than a blanket warning.
const isGas = (data) => data?.oil_gas_code === 'G';

function getStatusTooltip(statusNorm, data) {
  const gas = isGas(data);
  const n = data?.lease_well_count ?? null;

  switch (statusNorm) {
    case 'producing':
      return gas
        ? 'This well reported production in the last 12 months. RRC prorates gas per well, so this reflects this wellbore.'
        : n && n > 1
          ? `The lease reported production in the last 12 months. RRC reports oil volumes per lease, and this lease covers ${n.toLocaleString()} wells, so this may reflect other wellbores.`
          : 'The lease reported production in the last 12 months. RRC reports oil volumes per lease rather than per well.';
    case 'recently_active':
      return gas
        ? 'This well reported production 12–24 months ago, but not in the last 12.'
        : 'The lease reported production 12–24 months ago, but not in the last 12.';
    case 'inactive':
      return gas
        ? 'This well reported to RRC but recorded no volume in the last 24 months.'
        : 'The lease reported to RRC but recorded no volume in the last 24 months.';
    case 'no_production':
      return 'RRC wellbore status is "no production".';
    case 'plugged':
      return 'A plugging date is on file with RRC for this wellbore.';
    case 'temporarily_abandoned':
      return 'Idle under an active RRC extension (W-3X); may legally return to production.';
    case 'shut_in':
      return 'RRC wellbore status is shut in.';
    case 'injection':
      return 'Active injection well — does not report lease production.';
    case 'storage':
      return 'Gas or LPG storage well — does not report lease production.';
    case 'service':
      return 'Service well (water supply, observation, domestic) — does not report lease production.';
    case 'not_eligible':
      return 'RRC status: not eligible for allowable.';
    case 'drilling':
      return 'Currently drilling per RRC.';
    case 'permitted':
      return 'Permitted but not yet drilled per RRC.';
    case 'expired_permit':
      return 'RRC permit has expired.';
    case 'unknown':
      return 'No RRC wellbore status and no production record matched to this well. Not an assertion that the well is idle.';
    default:
      return undefined;
  }
}

// Which evidence produced the status. Surfacing this matters: a status
// derived from measured production is a far stronger claim than one
// inferred from a wellbore snapshot of unknown vintage.
const STATUS_SOURCE_LABELS = {
  plug_date: 'RRC plugging record',
  rrc_status_terminal: 'RRC wellbore status',
  rrc_wellbore_text: 'RRC wellbore status',
  production_same_code: 'production data',
  production_cross_code: 'production data (cross-completion)',
  production_prior_window: 'production data (12–24 mo)',
  production_zero_volume: 'production data (no volume reported)',
  no_signal: 'no data available',
};

const CONFLICT_MESSAGES = {
  plugged_but_status_producing:
    'RRC has a plugging date on file for this well, but its wellbore status still reads producing. The wellbore extract may predate the plugging, or the well may have been re-entered.',
  plugged_but_status_shut_in:
    'RRC has a plugging date on file for this well, but its wellbore status still reads shut in. The wellbore extract may predate the plugging.',
  plugged_but_status_injection:
    'RRC has a plugging date on file for this well, but its wellbore status still reads as an active injection well.',
  plugged_but_status_storage:
    'RRC has a plugging date on file for this well, but its wellbore status still reads as an active storage well.',
  plugged_but_status_service:
    'RRC has a plugging date on file for this well, but its wellbore status still reads as an active service well.',
};

function Badge({ label, color = 'gray', className = '', title }) {
  if (!label) return null;
  return (
    <span
      title={title}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${STATUS_COLORS[color] ?? STATUS_COLORS.gray} ${className}`}
    >
      {label}
    </span>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-800 first:border-t-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-800/40 transition-colors group"
      >
        <span className="text-[10px] font-semibold tracking-widest font-mono uppercase text-fg-secondary group-hover:text-slate-400 transition-colors">
          {title}
        </span>
        <svg
          className={`w-3 h-3 text-fg-secondary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

// emptyText distinguishes "RRC does not report this" from a value that
// failed to load. Lease number is absent on ~26% of wells at source.
function Field({ label, value, mono = false, emptyText = '—', title }) {
  const isEmpty = value == null || value === '';
  return (
    <div className="flex justify-between items-baseline gap-2 py-[3px]">
      <span className="text-[10px] text-fg-secondary shrink-0" title={title}>{label}</span>
      <span
        className={`text-[11px] text-right ${mono ? 'font-mono' : ''} ${isEmpty ? 'text-fg-secondary italic' : 'text-slate-200'}`}
      >
        {isEmpty ? emptyText : value}
      </span>
    </div>
  );
}

function Sparkline({ points, field }) {
  if (!points?.length) return null;
  const vals = points.map((p) => p[field] ?? 0);
  const max = Math.max(...vals, 1);
  const W = 72, H = 24;
  const step = W / (vals.length - 1 || 1);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${i * step},${H - (v / max) * H}`).join(' ');
  return (
    <svg width={W} height={H} className="overflow-visible">
      <path d={d} fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {vals.map((v, i) => (
        <circle key={i} cx={i * step} cy={H - (v / max) * H} r="2" fill="#34d399" />
      ))}
    </svg>
  );
}

const ALERT_STYLES = {
  critical: 'bg-red-950/60 border-red-800/60 text-red-300',
  warning: 'bg-amber-950/60 border-amber-800/60 text-amber-300',
  info: 'bg-slate-800/60 border-slate-700 text-slate-400',
};

function AlertBanner({ alert }) {
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded border text-[10px] mb-1.5 leading-snug ${ALERT_STYLES[alert.level] ?? ALERT_STYLES.info}`}>
      <span className="shrink-0 mt-[1px]">
        {alert.level === 'critical' ? '⚠' : alert.level === 'warning' ? '△' : 'ℹ'}
      </span>
      <span>{alert.message}</span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="px-4 py-3 space-y-2 animate-pulse">
      {[80, 60, 70, 50, 65].map((w, i) => (
        <div key={i} className="h-2.5 bg-slate-800 rounded" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

function ScoreBar({ score }) {
  if (score == null) return <span className="text-[11px] text-fg-secondary">No data</span>;
  const color = score >= 75 ? '#34d399' : score >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-mono text-slate-300 w-9 text-right">{Math.round(score)}%</span>
    </div>
  );
}

// Single source of truth for the operator risk bands, mirroring the CASE in
// aiprime.mv_operator_panel (failure_rate_lb: low >=0, medium >=5, high >=15,
// critical >=30). Used by the failure-rate caption so it cannot drift from the
// matview. Do NOT change these values without changing the matview's risk_tier.
const FAILURE_RATE_BANDS = [
  { tier: 'low',      min: 0,  label: 'Low' },
  { tier: 'medium',   min: 5,  label: 'Medium' },
  { tier: 'high',     min: 15, label: 'High' },
  { tier: 'critical', min: 30, label: 'Critical' },
];

// The tier is driven by failure_rate_lb (a Wilson lower bound), not the raw pass
// rate the panel leads with. Surface it as an annotation ON the risk badge.
function FailureRateCaption({ operator }) {
  // NULL guard: failure_rate_lb is NULL when there are 0 inspections. When there
  // is no compliance data, render nothing — the risk badge tooltip covers it.
  if (!operator || operator.has_compliance_data === false || operator.failure_rate_lb == null) {
    return null;
  }
  const lb = operator.failure_rate_lb;
  const tip = "Wilson lower bound on this operator's inspection failure rate — the conservative end of the confidence interval, so a small number of inspections cannot produce a flattering or alarming score. This is the value that sets the risk tier, not the raw pass rate above.";

  let text;
  if (operator.major_violation_count > 0) {
    // Major violation overrides the rate → tier is 'critical' regardless of lb.
    // Do NOT imply the rate set the tier.
    text = `Tier set by major violation(s); failure rate (lower bound): ${lb.toFixed(1)}`;
  } else {
    let idx = 0;
    for (let i = 0; i < FAILURE_RATE_BANDS.length; i++) {
      if (lb >= FAILURE_RATE_BANDS[i].min) idx = i;
    }
    const cur = FAILURE_RATE_BANDS[idx];
    const next = FAILURE_RATE_BANDS[idx + 1] ?? null; // nothing above critical
    text = `Failure rate (lower bound): ${lb.toFixed(1)} — ${cur.label} band`
         + (next ? `, ${next.label.toLowerCase()} at ${next.min.toFixed(1)}` : '');
  }

  return (
    <div className="text-[9px] text-fg-secondary/90 mb-2 leading-snug" title={tip}>
      {text}
    </div>
  );
}

export default function WellInfoPanel({ well, onClose }) {
  const { data, loading, error } = usePanelEnrichment(well);

  if (!well) return null;

  const p = well.properties ?? {};

  // Header — fall back to raw GeoJSON props while fetch is in-flight
  const displayName = data?.well_name ?? p.name ?? 'Well';
  const displayApi = data?.source_id ?? p.api ?? p.source_id ?? '';
  const statusBadge = data?.status_badge ?? null;
  const alerts = data?.compliance_alerts ?? [];

  // From the RRC wells ingest. status_norm is on the tile props immediately;
  // the rest resolve only after enrichment returns.
  const statusNorm = data?.status_norm ?? p.status_norm ?? null;
  const statusSource = data?.status_source ?? p.status_source ?? null;
  const statusConflict = data?.status_conflict ?? p.status_conflict ?? null;
  const plugDate = data?.plug_date ?? p.plug_date ?? null;

  const gas = isGas(data);
  const leaseWells = data?.lease_well_count ?? null;
  const sharedLease = !gas && leaseWells != null && leaseWells > 1;

  const statusTooltip = statusNorm ? getStatusTooltip(statusNorm, data) : undefined;

  return (
    <div className="absolute top-0 right-0 h-full w-[340px] bg-[#0d1117] border-l border-slate-800 z-[90]
                    flex flex-col transform transition-transform duration-300 translate-x-0 shadow-2xl">

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-800 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-white leading-tight truncate">
              {displayName}
            </div>
            {displayApi && (
              <div className="text-[10px] font-mono text-fg-secondary mt-0.5">
                API {displayApi}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded
                       text-fg-secondary hover:text-slate-300 hover:bg-slate-800 transition-colors"
          >
            <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M1 1l10 10M11 1L1 11" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {statusBadge ? (
            <Badge label={statusBadge.label} color={statusBadge.color} title={statusTooltip} />
          ) : statusNorm ? (
            <Badge label={statusNorm.replace(/_/g, ' ')} color="gray" title={statusTooltip} />
          ) : null}

          {data?.asset_type && (
            <Badge label={data.asset_type.replace('rrc_', '').replace(/_/g, ' ')} color="blue" />
          )}

          {data?.oil_gas_code && (
            <Badge
              label={gas ? 'Gas' : 'Oil'}
              color="gray"
              title={gas
                ? 'RRC prorates gas per well, so production figures are well-level.'
                : 'RRC prorates oil per lease, so production figures cover the whole lease.'}
            />
          )}

          {data && getFormationSource(data) && (
            <Badge label={getFormationSource(data)} color="amber" />
          )}

          {p.confidence != null && (
            <Badge label={`${Math.round(p.confidence * 100)}% conf`} color="gray" />
          )}
        </div>

        {/* Provenance for the status badge above. A status derived from
            measured production is a stronger claim than one inferred from
            a wellbore snapshot, and the user should be able to tell. */}
        {statusSource && (
          <div className="text-[9px] text-fg-secondary mt-1.5">
            Status from {STATUS_SOURCE_LABELS[statusSource] ?? 'an unrecognized source'}
          </div>
        )}

        {(data?.lat && data?.lon) && (
          <div className="text-[10px] font-mono text-fg-secondary mt-1.5">
            {data.lat.toFixed(5)}, {data.lon.toFixed(5)}
          </div>
        )}

        {statusConflict && (
          <div className="mt-2.5">
            <AlertBanner
              alert={{
                level: 'warning',
                message:
                  CONFLICT_MESSAGES[statusConflict] ??
                  'RRC records disagree about this well’s status.',
              }}
            />
          </div>
        )}
      </div>

      {/* ── SCROLLABLE BODY ────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-700">

        {alerts.length > 0 && (
          <div className="px-4 pt-3">
            {alerts.map((a, i) => <AlertBanner key={i} alert={a} />)}
          </div>
        )}

        {loading && !data && <Skeleton />}

        {error && !data && (
          <div className="px-4 py-3 text-[10px] text-red-400 bg-red-950/30 border-b border-red-900/40">
            Failed to load enrichment: {error}
          </div>
        )}

        {/* ── LOCATION ─────────────────────────────────────── */}
        {data && (
          <Section title="Location">
            <Field label="County" value={data.county} />
            <Field label="District" value={data.district_no} />
            <Field label="Field" value={data.field_name} />
            <Field label="Lease" value={data.lease_name} />
            <Field
              label="Lease No."
              value={data.lease_no}
              mono
              emptyText="not reported"
              title="RRC does not assign a lease number to every wellbore."
            />
            {leaseWells != null && (
              <Field
                label="Wells on lease"
                value={fmt(leaseWells)}
                title="Number of RRC surface wells sharing this lease number. Oil leases are proration units and can pool many wellbores; gas leases are usually a single well."
              />
            )}
            <Field label="Section / TWP" value={data.section_township_range} />
            <Field label="Survey" value={data.survey} emptyText="not reported" />
            <Field label="Abstract" value={data.abstract} mono emptyText="not reported" />
          </Section>
        )}

        {/* ── WELL ─────────────────────────────────────────── */}
        {data && (
          <Section title="Well">
            <Field label="Well No." value={data.well_no} mono />
            <Field label="Type" value={data.well_type} />
            <Field label="Spud date" value={fmtDate(data.spud_date)} />
            <Field label="Completion" value={fmtDate(data.completion_date)} />
            <Field label="Total depth" value={data.total_depth_ft ? `${fmt(data.total_depth_ft)} ft` : null} />
            <Field label="Formation" value={data ? getFormationSource(data) : null} />
            {plugDate && (
              <Field
                label="Plugged"
                value={fmtDate(plugDate)}
                title="Plugging date on file with RRC."
              />
            )}
          </Section>
        )}

        <WellDirectionalSection
          data={data}
          properties={p}
          tvdFt={
            data?.completion?.true_vertical_depth
            ?? data?.completion?.total_depth
            ?? data?.total_depth_ft
            ?? null
          }
          formation={data ? getFormationSource(data) : null}
        />

        {/* ── OPERATOR ─────────────────────────────────────── */}
        {data?.operator && (
          <Section title="Operator">
            <div className="mb-2">
              <div className="text-[12px] text-slate-200 font-medium leading-tight">
                {data.operator.operator_name}
              </div>
              <div className="text-[10px] text-fg-secondary mt-0.5">
                #{data.operator.operator_no}
                {data.operator.city ? ` · ${data.operator.city}, ${data.operator.state ?? ''}` : ''}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <Badge
                label={data.operator.risk_badge?.label ?? data.operator.risk_tier}
                color={data.operator.risk_badge?.color ?? 'gray'}
                title={data.operator.has_compliance_data === false
                  ? 'No RRC inspection record for this operator — not a finding either way.'
                  : 'Derived from the lower bound of this operator’s inspection failure rate, so it is not inflated by inspection volume.'}
              />
              <Badge
                label={data.operator.p5_active ? 'Active' : 'Inactive'}
                color={data.operator.p5_active ? 'green' : 'gray'}
                title="P-5 organization report status with RRC."
              />
            </div>

            <FailureRateCaption operator={data.operator} />

            <div className="mb-2">
              <div className="text-[10px] text-fg-secondary mb-1"
                   title="Share of all RRC inspections of this operator's wells with no non-compliance recorded — operator-wide, not this wellbore. Raw rate, not adjusted for sample size; the risk tier above uses a statistical lower bound on the failure rate instead.">
                Inspection pass rate
              </div>
              <ScoreBar score={data.operator.inspection_pass_rate ?? data.operator.compliance_score} />
            </div>

            <Field label="Inspections (total)" value={fmt(data.operator.inspection_count_total)} />
            <Field label="Violations (total)" value={fmt(data.operator.violation_count_total)} />
            <Field label="Major violations" value={fmt(data.operator.major_violation_count)} />
            <Field label="Non-compliant" value={fmt(data.operator.non_compliant_inspections)} />
            <Field label="Last inspection" value={fmtDate(data.operator.last_inspection_date)} />

            <div className="text-[9px] text-fg-secondary/90 mt-2 leading-snug">
              Operator of record with RRC, which may differ from the lessee.
            </div>
          </Section>
        )}

        {/* ── PRODUCTION ───────────────────────────────────── */}
        {data?.production && (
          <Section title={gas ? 'Production (24-month)' : 'Production (lease, 24-month)'}>
            {/* Only oil needs the aggregation caveat: RRC prorates gas per
                well. Where the lease well count is known, state it — 80% of
                oil leases hold 10 wells or fewer, so a blanket warning
                overstates the problem for most wells and understates it for
                the few hundred unitized floods. */}
            {!gas && (
              <div className="text-[9px] text-fg-secondary/90 mb-2.5 leading-snug">
                {sharedLease
                  ? `RRC reports oil volumes by lease. This lease covers ${fmt(leaseWells)} wells, so these totals are not this wellbore alone.`
                  : 'RRC reports oil volumes by lease rather than by well.'}
              </div>
            )}

            {data.production.sparkline_3mo?.length > 0 && (
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1">Oil trend</div>
                  <Sparkline points={data.production.sparkline_3mo} field="oil" />
                </div>
                <div className="text-right">
                  <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1">Latest month</div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {fmtDate(data.production.latest_month)}
                  </div>
                </div>
              </div>
            )}
            <Field label="Oil (24mo)" value={data.production.oil_24mo_bbl ? `${fmt(data.production.oil_24mo_bbl)} bbl` : null} />
            <Field label="Gas (24mo)" value={data.production.gas_24mo_mcf ? `${fmt(data.production.gas_24mo_mcf)} Mcf` : null} />
            <Field label="Condensate" value={data.production.cond_24mo_bbl ? `${fmt(data.production.cond_24mo_bbl)} bbl` : null} />
            <Field label="Months reporting" value={data.production.months_reporting} />
            {data.production.peak_year && (
              <>
                <div className="border-t border-slate-800/80 my-2" />
                <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1.5">
                  Peak year ({data.production.peak_year})
                </div>
                <Field label="Peak oil" value={data.production.peak_oil_bbl ? `${fmt(data.production.peak_oil_bbl)} bbl` : null} />
                <Field label="Peak gas" value={data.production.peak_gas_mcf ? `${fmt(data.production.peak_gas_mcf)} Mcf` : null} />
              </>
            )}
          </Section>
        )}

        {/* ── COMPLETION ───────────────────────────────────── */}
        {data?.completion && (
          <Section title="Completion" defaultOpen={false}>
            <Field label="Spud date" value={fmtDate(data.completion.spud_date)} />
            <Field label="Comp. date" value={fmtDate(data.completion.completion_date)} />
            <Field label="Total depth" value={data.completion.total_depth ? `${fmt(data.completion.total_depth)} ft` : null} />
            <Field label="True vert. depth" value={data.completion.true_vertical_depth ? `${fmt(data.completion.true_vertical_depth)} ft` : null} />
            <Field label="Lateral length" value={data.completion.lateral_length ? `${fmt(data.completion.lateral_length)} ft` : null} />
            <Field label="Stages" value={fmt(data.completion.stage_count)} />
            <Field label="Perfs" value={fmt(data.completion.perf_count)} />
            <Field label="Proppant" value={data.completion.proppant_amount ? `${fmt(data.completion.proppant_amount)} lbs` : null} />
            <Field label="Primary formation" value={data.completion.primary_formation} />
            <Field label="Initial prod." value={data.completion.initial_production ? `${fmt(data.completion.initial_production, 1)} bbl/d` : null} />

            {data.completion.formations?.length > 0 && (
              <>
                <div className="border-t border-slate-800/80 my-2" />
                <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1.5">Formations</div>
                {data.completion.formations.map((f, i) => (
                  <div key={i} className="flex items-baseline justify-between py-[3px]">
                    <span className="text-[10px] text-slate-300 flex items-center gap-1">
                      {f.productive && <span className="w-1 h-1 rounded-full bg-emerald-400 inline-block shrink-0" />}
                      {f.formation_name}
                    </span>
                    <span className="text-[10px] font-mono text-fg-secondary shrink-0">
                      {f.formation_depth != null ? `${fmt(f.formation_depth)} ft` : '—'}
                    </span>
                  </div>
                ))}
              </>
            )}
          </Section>
        )}

        {/* ── COMPLIANCE ───────────────────────────────────── */}
        {data && (data.violations?.length > 0 || data.inspections?.length > 0) && (
          <Section title="Compliance" defaultOpen={data.violations?.length > 0}>

            {data.violations?.length > 0 && (
              <>
                <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1.5">Violations</div>
                <div className="space-y-1.5 mb-3">
                  {data.violations.map((v, i) => (
                    <div key={i} className="rounded bg-slate-800/50 border border-slate-700/50 px-2.5 py-2">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="text-[10px] text-slate-200 font-medium truncate">
                          {v.violated_rule_desc ?? v.violated_rule ?? 'Violation'}
                        </span>
                        <Badge
                          label={v.is_major ? 'Major' : 'Minor'}
                          color={v.is_major ? 'red' : 'gray'}
                        />
                      </div>
                      <div className="text-[9px] text-fg-secondary">
                        {fmtDate(v.violation_date)}
                        {v.last_enf_action ? ` · ${v.last_enf_action}` : ''}
                      </div>
                      {v.compliant_on_reinsp && (
                        <div className="text-[9px] text-emerald-500 mt-0.5">
                          Compliant on reinspection
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {data.inspections?.length > 0 && (
              <>
                <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wider mb-1.5">Recent inspections</div>
                <div className="space-y-1">
                  {data.inspections.map((ins, i) => (
                    <div key={i} className="flex items-start justify-between gap-2 py-[3px]">
                      <div>
                        <div className="text-[10px] text-slate-300">
                          {ins.field_name ?? ins.lease_name ?? 'Inspection'}
                        </div>
                        <div className="text-[9px] text-fg-secondary">
                          {fmtDate(ins.inspection_date)}
                          {ins.well_no ? ` · Well ${ins.well_no}` : ''}
                        </div>
                      </div>
                      <Badge
                        label={ins.compliance ? 'Pass' : 'Fail'}
                        color={ins.compliance ? 'green' : 'red'}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </Section>
        )}

        {/* ── UIC ──────────────────────────────────────────── */}
        {data?.uic && (
          <Section title="UIC Injection" defaultOpen={false}>
            <Field label="Well type" value={data.uic.well_type_norm ?? data.uic.well_type_code} />
            <Field label="Status" value={data.uic.is_active ? 'Active' : 'Inactive'} />
            <Field label="Spud date" value={fmtDate(data.uic.spud_date)} />
            <Field label="Completion" value={fmtDate(data.uic.completion_date)} />
            <Field label="Approval date" value={fmtDate(data.uic.approval_date)} />
            <Field label="Lease No." value={data.uic.lease_no} mono emptyText="not reported" />
          </Section>
        )}

        <div className="h-6" />
      </div>
    </div>
  );
}
